import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    HeartPulse, RefreshCw, Search, Plus, X, Star,
} from "lucide-react";
import { Select } from "antd";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { DetailDrawer } from "../../components/engage/DetailDrawer";
import {
    ENGAGE_PATIENTS_URL, ENGAGE_PATIENT_URL,
    ENGAGE_REFERRING_DOCTORS_URL,
} from "../../constants/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// ─── Constants ──────────────────────────────────────────────────────────────

const LANGUAGE_LABELS = {
    en: 'English', ta: 'Tamil', hi: 'Hindi', te: 'Telugu', ml: 'Malayalam', kn: 'Kannada',
};

const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'ta', label: 'Tamil' },
    { value: 'hi', label: 'Hindi' },
    { value: 'te', label: 'Telugu' },
    { value: 'ml', label: 'Malayalam' },
    { value: 'kn', label: 'Kannada' },
];

const GENDER_OPTIONS = [
    { value: '',       label: '— unspecified —' },
    { value: 'male',   label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other',  label: 'Other' },
];

// ─── Main component ────────────────────────────────────────────────────────

// ─── DetailDrawer field schema ─────────────────────────────────────────────
const PATIENT_FIELDS = [
    { key: 'id',                 label: 'ID',                type: 'readonly' },
    { key: 'uhid',               label: 'UHID',              type: 'text' },
    { key: 'emedhub_patient_id', label: 'eMedHub patient id',type: 'number' },
    { key: 'first_name',         label: 'First name',        type: 'text' },
    { key: 'last_name',          label: 'Last name',         type: 'text' },
    { key: 'gender',             label: 'Gender',            type: 'select',
      options: [
        { value: '',       label: '— unspecified —' },
        { value: 'male',   label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other',  label: 'Other' },
      ] },
    { key: 'date_of_birth',      label: 'Date of birth',     type: 'date' },
    { key: 'mobile',             label: 'Mobile',            type: 'mobile' },
    { key: 'alt_mobile',         label: 'Alt mobile',        type: 'text' },
    { key: 'email',              label: 'Email',             type: 'email' },
    { key: 'address',            label: 'Address',           type: 'textarea' },
    { key: 'city',               label: 'City',              type: 'text' },
    { key: 'state',              label: 'State',             type: 'text' },
    { key: 'pincode',            label: 'Pincode',           type: 'text' },
    { key: 'preferred_language', label: 'Preferred language',type: 'select',
      options: LANGUAGE_OPTIONS },
    { key: 'prefer_whatsapp',    label: 'WhatsApp OK',       type: 'boolean' },
    { key: 'prefer_sms',         label: 'SMS OK',            type: 'boolean' },
    { key: 'prefer_email',       label: 'Email OK',          type: 'boolean' },
    { key: 'prefer_call',        label: 'Call OK',           type: 'boolean' },
    { key: 'consent_marketing',  label: 'Consent — marketing', type: 'boolean' },
    { key: 'consent_data_share', label: 'Consent — data share', type: 'boolean' },
    { key: 'is_vip',             label: 'VIP',               type: 'boolean' },
    { key: 'tags',               label: 'Tags',              type: 'tags' },
    { key: 'family_head_name',   label: 'Family head',       type: 'readonly' },
    { key: 'created_date',       label: 'Registered',        type: 'datetime' },
    { key: 'modified_date',      label: 'Last updated',      type: 'datetime' },
];

const PatientsList = () => {
    const navigate = useNavigate();
    const { fetch: apiFetch, create, update, remove } = useApiMethods({ throwOnFail: true });

    const [rows, setRows]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filters, setFilters]   = useState({ mobile: '', uhid: '', is_vip: '', tag: '' });

    // Row-click detail drawer
    const [selected, setSelected] = useState(null);

    const [newModalOpen, setNewModalOpen] = useState(false);
    const [newForm, setNewForm] = useState({
        first_name: '', last_name: '', mobile: '', email: '',
        gender: '', preferred_language: 'en', is_vip: false, tags: '',
        referring_doctor_id: null, clinical_reason: '',
    });
    const [newBusy, setNewBusy] = useState(false);
    const [doctorOptions, setDoctorOptions] = useState([]);

    useEffect(() => {
        apiFetch(ENGAGE_REFERRING_DOCTORS_URL)
            .then(res => setDoctorOptions(
                (res?.results || []).map(d => ({
                    value: d.id,
                    label: `Dr. ${d.name}${d.specialty ? ' — ' + d.specialty : ''}${d.mobile ? ' · ' + d.mobile : ''}`,
                }))
            ))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.mobile) params.mobile = filters.mobile;
            if (filters.uhid)   params.uhid   = filters.uhid;
            if (filters.is_vip) params.is_vip = filters.is_vip;
            if (filters.tag)    params.tag    = filters.tag;
            const res = await apiFetch(ENGAGE_PATIENTS_URL, params);
            setRows(res?.results || []);
        } catch (e) {
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async () => {
        if (!newForm.first_name || !newForm.mobile) {
            ToastMsg('error', 'First name and mobile are required');
            return;
        }
        setNewBusy(true);
        try {
            const payload = {
                first_name:         newForm.first_name,
                last_name:          newForm.last_name,
                mobile:             newForm.mobile,
                email:              newForm.email,
                gender:             newForm.gender,
                preferred_language: newForm.preferred_language,
                is_vip:             newForm.is_vip,
                tags:               newForm.tags
                    ? newForm.tags.split(',').map(t => t.trim()).filter(Boolean)
                    : [],
                ...(newForm.referring_doctor_id
                    ? { referring_doctor_id: newForm.referring_doctor_id,
                        clinical_reason:    newForm.clinical_reason }
                    : {}),
            };
            await create(ENGAGE_PATIENTS_URL, payload);
            ToastMsg('success', newForm.referring_doctor_id
                ? 'Patient created & linked to doctor'
                : 'Patient created');
            setNewModalOpen(false);
            setNewForm({
                first_name: '', last_name: '', mobile: '', email: '',
                gender: '', preferred_language: 'en', is_vip: false, tags: '',
                referring_doctor_id: null, clinical_reason: '',
            });
            load();
        } catch (e) {
            ToastMsg('error', 'Failed to create patient');
        } finally {
            setNewBusy(false);
        }
    };

    return (
        <ContainerCard>
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                    <HeartPulse className="w-5 h-5 text-rose-600" />
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Patients</h1>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({rows.length})</span>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm" onClick={() => setNewModalOpen(true)}>
                        <Plus className="w-4 h-4 mr-1" /> New Patient
                    </Button>
                </div>
            </div>

            {/* ── Filters ──────────────────────────────────────────────── */}
            <div className="flex flex-wrap gap-2 mb-3 items-center px-1">
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                    <Input
                        placeholder="Search mobile"
                        value={filters.mobile}
                        onChange={(e) => setFilters(f => ({ ...f, mobile: e.target.value }))}
                        className="pl-8 h-9 w-48"
                    />
                </div>
                <Input
                    placeholder="UHID"
                    value={filters.uhid}
                    onChange={(e) => setFilters(f => ({ ...f, uhid: e.target.value }))}
                    className="h-9 w-40"
                />
                <Select
                    style={{ minWidth: 140 }}
                    value={filters.is_vip}
                    onChange={(v) => setFilters(f => ({ ...f, is_vip: v }))}
                    options={[
                        { value: '',      label: 'All patients' },
                        { value: 'true',  label: 'VIP only' },
                        { value: 'false', label: 'Non-VIP' },
                    ]}
                    placeholder="VIP filter"
                />
                <Input
                    placeholder="Tag (e.g. diabetic)"
                    value={filters.tag}
                    onChange={(e) => setFilters(f => ({ ...f, tag: e.target.value }))}
                    className="h-9 w-48"
                />
                {(filters.mobile || filters.uhid || filters.is_vip || filters.tag) && (
                    <Button variant="ghost" size="sm" onClick={() => setFilters({ mobile: '', uhid: '', is_vip: '', tag: '' })}>
                        <X className="w-3 h-3 mr-1" /> Clear
                    </Button>
                )}
            </div>

            {/* ── Table ────────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Name</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Mobile</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">UHID</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Gender</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Language</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Tags</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">VIP</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Registered</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="px-3 py-8 text-center text-gray-500">Loading…</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-3 py-8 text-center text-gray-500 italic">
                                    No patients match the current filters.
                                </td>
                            </tr>
                        ) : rows.map((p) => (
                            <tr key={p.id}
                                onClick={() => navigate(`/engage/patients/${p.id}`)}
                                className="cursor-pointer border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                    <div className="font-medium">{p.first_name} {p.last_name}</div>
                                    {p.email && <div className="text-xs text-gray-500">{p.email}</div>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">
                                    {p.mobile}
                                    {p.alt_mobile && <div className="text-gray-400">{p.alt_mobile}</div>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">
                                    {p.uhid || <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 capitalize">
                                    {p.gender || <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                    {LANGUAGE_LABELS[p.preferred_language] || p.preferred_language}
                                </td>
                                <td className="px-3 py-2">
                                    {p.tags && p.tags.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {p.tags.slice(0, 3).map((t, i) => (
                                                <span key={i} className="inline-block px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                                                    {t}
                                                </span>
                                            ))}
                                            {p.tags.length > 3 && (
                                                <span className="text-xs text-gray-400">+{p.tags.length - 3}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-xs">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    {p.is_vip ? (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-medium">
                                            <Star className="w-3 h-3 fill-current" /> VIP
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 text-xs">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-gray-500 text-xs">
                                    {p.created_date ? dayjs(p.created_date).fromNow() : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── Detail drawer ────────────────────────────────────────── */}
            <DetailDrawer
                open={!!selected}
                onClose={() => setSelected(null)}
                record={selected}
                title={selected && `${selected.first_name} ${selected.last_name || ''}`.trim()}
                subtitle="Patient"
                fields={PATIENT_FIELDS}
                onSave={async (patch) => {
                    try {
                        const body = { ...selected, ...patch };
                        await update(ENGAGE_PATIENT_URL(selected.id), body);
                        ToastMsg('success', 'Patient updated');
                        setSelected(null);
                        load();
                    } catch (e) {
                        ToastMsg('error', 'Failed to update patient');
                    }
                }}
                onDelete={async () => {
                    try {
                        await remove(ENGAGE_PATIENT_URL(selected.id));
                        ToastMsg('success', 'Patient deleted');
                        load();
                    } catch (e) {
                        ToastMsg('error', 'Failed to delete');
                    }
                }}
            />

            {/* ── New patient modal ────────────────────────────────────── */}
            <DialogModal
                open={newModalOpen}
                onClose={() => setNewModalOpen(false)}
                onSave={handleCreate}
                title="New Patient"
                saveLabel={newBusy ? 'Creating…' : 'Create'}
                loading={newBusy}
                width="520px"
            >
                <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">First name *</label>
                            <Input
                                value={newForm.first_name}
                                onChange={(e) => setNewForm(f => ({ ...f, first_name: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Last name</label>
                            <Input
                                value={newForm.last_name}
                                onChange={(e) => setNewForm(f => ({ ...f, last_name: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Mobile *</label>
                            <Input
                                value={newForm.mobile}
                                onChange={(e) => setNewForm(f => ({ ...f, mobile: e.target.value }))}
                                placeholder="+919xxxxxxxxx"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Email</label>
                            <Input
                                value={newForm.email}
                                onChange={(e) => setNewForm(f => ({ ...f, email: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Gender</label>
                            <Select
                                className="w-full"
                                value={newForm.gender}
                                onChange={(v) => setNewForm(f => ({ ...f, gender: v }))}
                                options={GENDER_OPTIONS}
                                allowClear
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Preferred language</label>
                            <Select
                                className="w-full"
                                value={newForm.preferred_language}
                                onChange={(v) => setNewForm(f => ({ ...f, preferred_language: v }))}
                                options={LANGUAGE_OPTIONS}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Tags (comma-separated)</label>
                        <Input
                            value={newForm.tags}
                            onChange={(e) => setNewForm(f => ({ ...f, tags: e.target.value }))}
                            placeholder="e.g. diabetic, cardiac"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Referring doctor (optional)</label>
                        <Select
                            className="w-full"
                            showSearch
                            allowClear
                            value={newForm.referring_doctor_id || undefined}
                            placeholder="Select a referring doctor…"
                            optionFilterProp="label"
                            options={doctorOptions}
                            onChange={(v) => setNewForm(f => ({
                                ...f,
                                referring_doctor_id: v || null,
                                clinical_reason: v ? f.clinical_reason : '',
                            }))}
                            notFoundContent={doctorOptions.length === 0 ? 'No referring doctors yet' : 'No match'}
                        />
                    </div>
                    {newForm.referring_doctor_id && (
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Clinical reason (optional)</label>
                            <Input
                                value={newForm.clinical_reason}
                                onChange={(e) => setNewForm(f => ({ ...f, clinical_reason: e.target.value }))}
                                placeholder="Why did the doctor refer this patient?"
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="new-patient-vip"
                            checked={newForm.is_vip}
                            onChange={(e) => setNewForm(f => ({ ...f, is_vip: e.target.checked }))}
                            className="rounded"
                        />
                        <label htmlFor="new-patient-vip" className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-500" /> Mark as VIP
                        </label>
                    </div>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default PatientsList;
